import { DiscoverItemCard, DiscoverItemType, HubResourceSection as HubResourceSectionType } from 'utils/gloo-types';
import DiscoverCardPreview from 'pages/_AdminAuthoringPages/_components/DiscoverCardPreview';
import { HubPreviewView } from 'pages/_AdminAuthoringPages/_components/HubPreview';
import { ResponsiveType } from 'react-multi-carousel';
import { tailwindBreakpointLt } from 'utils/common';
import { discoverItemCardForKit, discoverItemCardForTemplate, useDiscoverItemAuthorSelect } from 'utils/discover';
import { TailwindBreakpoint } from 'utils/frontendTypes';
import { useTailwindBreakpoint } from 'utils/hooks';
import MultiCarousel from 'components/molecules/MultiCarousel';
import HubSectionDescription from './HubSectionDescription';
import HubSectionTitle from './HubSectionTitle';

interface HubResourceSectionProps {
  hubResourceSection: HubResourceSectionType;
  previewView: HubPreviewView;
}

const resourceCarouselResponsiveBreakpoints: ResponsiveType = {
  xs: {
    breakpoint: { min: 0, max: 640 },
    items: 1,
    partialVisibilityGutter: 60,
  },
  sm: {
    breakpoint: { min: 640, max: 768 },
    items: 2,
    partialVisibilityGutter: 60,
  },
  md: {
    breakpoint: { min: 768, max: 1024 },
    items: 2,
    partialVisibilityGutter: 60,
  },
  lg: {
    breakpoint: { min: 1024, max: 1280 },
    items: 3,
    partialVisibilityGutter: 60,
  },
  xl: {
    breakpoint: { min: 1280, max: 1536 },
    items: 3,
    partialVisibilityGutter: 60,
  },
  xxl: {
    breakpoint: { min: 1536, max: 9999 },
    items: 6,
    partialVisibilityGutter: 10,
  },
};

const resourceCarouselMobileViewBreakpoints: ResponsiveType = {
  all: {
    breakpoint: { min: 0, max: 9999 },
    items: 1,
    partialVisibilityGutter: 60,
  },
};

const resourceCarouselDesktopViewBreakpoints: ResponsiveType = {
  all: {
    breakpoint: { min: 0, max: 9999 },
    items: 3,
    partialVisibilityGutter: 60,
  },
};

const RESOURCE_CAROUSEL_PADDING = {
  DESKTOP: '163px',
  MOBILE: '111px',
} as const;

const HubResourceSection = ({
  hubResourceSection: { title, description, titleColor, descriptionColor, resources },
  previewView,
}: HubResourceSectionProps) => {
  const tailwindBreakpoint = useTailwindBreakpoint();
  const handleDiscoverItemSelected = useDiscoverItemAuthorSelect();

  const resourceCards = resources?.map((resource) => {
    let discoverItemCard: DiscoverItemCard;
    switch (resource.type) {
      case DiscoverItemType.KIT:
        discoverItemCard = discoverItemCardForKit(resource.kit);
        break;
      case DiscoverItemType.TEMPLATE:
        discoverItemCard = discoverItemCardForTemplate(resource.template);
        break;
    }
    return (
      <DiscoverCardPreview
        key={discoverItemCard.itemId}
        className="w-full h-full px-2 py-4"
        onClick={handleDiscoverItemSelected}
        itemCard={discoverItemCard}
      />
    );
  });

  let resourceCarouselWidth: string;
  switch (previewView) {
    case HubPreviewView.Desktop:
      resourceCarouselWidth = `calc(1280px - ${RESOURCE_CAROUSEL_PADDING.DESKTOP})`;
      break;
    case HubPreviewView.Mobile:
      resourceCarouselWidth = `calc(640px - ${RESOURCE_CAROUSEL_PADDING.MOBILE})`;
      break;
    default:
      resourceCarouselWidth = `calc(100vw - ${
        tailwindBreakpointLt(tailwindBreakpoint, TailwindBreakpoint.XL)
          ? RESOURCE_CAROUSEL_PADDING.MOBILE
          : RESOURCE_CAROUSEL_PADDING.DESKTOP
      })`;
      break;
  }

  const resourceCarouselBreakpoints = !previewView
    ? resourceCarouselResponsiveBreakpoints
    : previewView === HubPreviewView.Desktop
      ? resourceCarouselDesktopViewBreakpoints
      : resourceCarouselMobileViewBreakpoints;

  return (
    <div>
      <HubSectionTitle title={title} titleColor={titleColor} />
      <HubSectionDescription description={description} descriptionColor={descriptionColor} />
      <div className="m-auto">
        <MultiCarousel
          items={resourceCards}
          responsiveObject={resourceCarouselBreakpoints}
          width={resourceCarouselWidth}
        />
      </div>
    </div>
  );
};

export default HubResourceSection;
